import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleVO } from '@article/model/vo/article.vo';
import { MountingRailArticlePostParseProcessor } from '@project/components/article/processor/postparse/mounting-rail-post-parse.processor';
import { DrillingHoleArticlePostParseProcessor } from '@project/components/article/processor/postparse/drilling-hole-post-parse.processor';
import { EmptyZoneArticlePostParseProcessor } from '@project/components/article/processor/postparse/empty-zone-article-post-parse.processor';
import { BlockingZoneArticlePostParseProcessor } from '@project/components/article/processor/postparse/blocking-zone-article-post-parse.processor';
import { CustomArticlePostParseProcessor } from '@project/components/article/processor/postparse/custom-article-post-parse.processor';
import { BridgeArticlePostParseProcessor } from '@project/components/article/processor/postparse/bridge-article-post-parse.processor';
import { WireBridgeArticlePostParseProcessor } from '@project/components/article/processor/postparse/wire-bridge-article-post-parse.processor';
import { ArticleUtils } from '@article/util/article-utils';
import { DynamicLabelingArticlePostParseProcessor } from '@project/components/article/processor/postparse/dynamic-labeling-article-post-parse.processor';
import { BusbarOrCoverArticlePostParseProcessor } from '@project/components/article/processor/postparse/busbar-or-cover-article-post-parse-processor';
import { CoverLabelStripPostParseProcessor } from '@project/components/article/processor/postparse/cover-label-strip-post-parse-processor';
import { UserModel } from '@user/model/user.model';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { Injectable } from '@angular/core';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ProjectUserModel } from '@project/model/project-user.model';

@Injectable({ providedIn: 'root' })
export class ArticlePostParseProcessor {
  private readonly DUMMY_TYPE_COVER_LABEL_STRIP: string = 'CoverLabelStrip';

  // Rendering order is set to handle the transparency of the objects. For example to make drilling holes visible threw cover or
  // empty/blocking zones.
  private readonly DRILLING_HOLE_RENDER_ORDER: number = 1;
  private readonly ZONE_RENDER_ORDER: number = 2;
  private readonly COVER_RENDER_ORDER: number = 3;

  private _coverLabelStripPostParseProcessor: CoverLabelStripPostParseProcessor;
  private _emptyZoneArticlePostParseProcessor: EmptyZoneArticlePostParseProcessor;
  private _blockingZoneArticlePostParseProcessor: BlockingZoneArticlePostParseProcessor;
  private _dynamicLabelingArticlePostParseProcessor: DynamicLabelingArticlePostParseProcessor;
  private _busbarOrCoverArticlePostParseProcessor: BusbarOrCoverArticlePostParseProcessor;

  constructor(private connectionInfoUtils: ConnectionInfoUtils, private _userModel: UserModel, private _projectUserModel: ProjectUserModel, private _componentUtils: ContainerService) {
    this._coverLabelStripPostParseProcessor = new CoverLabelStripPostParseProcessor(_componentUtils);
    this._emptyZoneArticlePostParseProcessor = new EmptyZoneArticlePostParseProcessor(_componentUtils);
    this._blockingZoneArticlePostParseProcessor = new BlockingZoneArticlePostParseProcessor(_componentUtils);
    this._dynamicLabelingArticlePostParseProcessor = new DynamicLabelingArticlePostParseProcessor();
    this._busbarOrCoverArticlePostParseProcessor = new BusbarOrCoverArticlePostParseProcessor();
  }

  public parse(articleContainer: ArticleContainer): void {
    if (!articleContainer.article || !articleContainer.article.type) {
      return;
    }
    const article: ArticleVO = articleContainer.article;
    const articleType: string = this.getArticleType(article);
    switch (articleType) {
      case ArticleTypeConstants.TYPE_MOUNTING_RAIL:
        MountingRailArticlePostParseProcessor.process(articleContainer, article, this._componentUtils, this.connectionInfoUtils);
        break;
      case ArticleTypeConstants.TYPE_DRILLING_HOLE:
        DrillingHoleArticlePostParseProcessor.process(articleContainer, article, this.DRILLING_HOLE_RENDER_ORDER);
        break;
      case ArticleTypeConstants.TYPE_EMPTY_ZONE:
        this._emptyZoneArticlePostParseProcessor.process(articleContainer, this.ZONE_RENDER_ORDER);
        break;
      case ArticleTypeConstants.TYPE_BLOCKING_ZONE:
        this._blockingZoneArticlePostParseProcessor.process(articleContainer, this.ZONE_RENDER_ORDER);
        break;
      case ArticleTypeConstants.TYPE_CUSTOM_ARTICLE:
        CustomArticlePostParseProcessor.process(articleContainer, this._userModel, this._projectUserModel);
        break;
      case ArticleTypeConstants.TYPE_BRIDGE:
        BridgeArticlePostParseProcessor.process(articleContainer, article);
        break;
      case ArticleTypeConstants.TYPE_WIRE_BRIDGE:
        WireBridgeArticlePostParseProcessor.process(articleContainer);
        break;
      case ArticleTypeConstants.TYPE_LABELING:
        if (ArticleUtils.isDynamicArticle(article)) {
          this._dynamicLabelingArticlePostParseProcessor.process(articleContainer, article);
        }
        break;
      case ArticleTypeConstants.TYPE_N_BUSBAR:
      case ArticleTypeConstants.TYPE_COVER:
        this._busbarOrCoverArticlePostParseProcessor.process(articleContainer, article, this.COVER_RENDER_ORDER);
        break;
      case this.DUMMY_TYPE_COVER_LABEL_STRIP:
        this._coverLabelStripPostParseProcessor.process(articleContainer, article);
        break;
    }
  }

  private getArticleType(article: ArticleVO): string {
    let articleType: string = article.type.displayName;
    if (articleType === ArticleTypeConstants.TYPE_LABELING && ArticleUtils.isCoverLabelStripArticle(article)) {
      articleType = this.DUMMY_TYPE_COVER_LABEL_STRIP;
    }
    return articleType;
  }
}
